import { Button, Space, Typography, Popconfirm } from "antd";
import ListTable from "common/ListTable";
import { IMPORT_MACHINE_INFO } from "apiServices/API_ENDPOINTS";
import { deleteData, getData } from "apiServices/common";
import AppContent from "common/AppContent";
import HandleNotification from "common/Notification";
import AppPageHeader from "common/PageHeader";
import ExtraContent from "components/Configuration/CommonComponents/ExtraContent";
import FilterComponent from "components/Configuration/CommonComponents/FilterComponent";
import moment from "moment";

import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router";
import { MACHINE_LINE_ISSUES_VIEW_PERMISSION } from "routes/permissions";
import { cglFormatDate, hasPermission } from "utils/functions";

const MachineIssues = (props) => {
  // Router
  const location = useLocation();
  const routerPath = location ? location.pathname : null;
  const navigate = useNavigate();
  const { id } = useParams();

  // States
  const [showFilter, setShowFilter] = useState(
    localStorage.getItem("filter_status") && routerPath
      ? JSON.parse(localStorage.getItem("filter_status"))[routerPath]
      : false,
  );
  const [page, setPage] = useState(1);
  const [refresh, setRefresh] = useState(0);
  const [tableData, setTableData] = useState();

  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [filterValues, setFilterValues] = useState({});

  // Antd
  const { Title } = Typography;

  // Filter open/close status update in localStorage
  useEffect(() => {
    const filterStatus = localStorage.getItem("filter_status");
    const parsedFilterStatus = filterStatus ? JSON.parse(filterStatus) : {};

    if (showFilter) {
      parsedFilterStatus[routerPath] = true;
      localStorage.setItem("filter_status", JSON.stringify(parsedFilterStatus));
    } else {
      parsedFilterStatus[routerPath] = false;
      localStorage.setItem("filter_status", JSON.stringify(parsedFilterStatus));
    }
  }, [showFilter, routerPath, location]);

  const onFilterChange = (values) => {
    setFilterValues(values);
  };

  const fetchData = useCallback(
    async (filterValues) => {
      const query = `/api/digitalization/machine-maintenance/machine-issues/web`;
      const bodyData = {
        page: page,
        per_page: filterValues?.per_page || 10,
        ...(filterValues?.issue_date && {
          issue_date: moment(filterValues.issue_date).format("YYYY-MM-DD"),
        }),
        ...(filterValues?.machine_information_id && {
          machine_information_id: filterValues.machine_information_id,
        }),
        ...(filterValues?.sewing_line_id && {
          sewing_line_id: filterValues.sewing_line_id,
        }),
        ...(filterValues?.machine_line_id && {
          machine_line_id: filterValues.machine_line_id,
        }),
        ...(filterValues?.machine_fault_type_id && {
          machine_fault_type_id: filterValues.machine_fault_type_id,
        }),
        ...(filterValues?.status !== undefined && {
          status: filterValues.status,
        }),
        ...(filterValues?.is_assigned !== undefined && {
          is_assigned: filterValues.is_assigned,
        }),
        // ...(filterValues?.line_id && { line_id: filterValues?.line_id }),
        // ...(filterValues?.style_id && { style_id: filterValues?.style_id }),
        // ...(filterValues?.po_map_id && { po_map_id: filterValues?.po_map_id }),
      };

      const response = await getData(query, false, bodyData);

      if (response && response?.data?.code === 200) {
        setTableData(response?.data?.data?.data);
        setPageSize(response?.data?.data?.per_page);
        setTotal(response?.data?.data?.total);
      } else {
        HandleNotification("error", "Error", "Something went wrong");
      }
    },
    [page],
  );

  useEffect(() => {
    const value = {
      ...filterValues,
      page: page,
    };
    fetchData(value);
  }, [refresh, page, pageSize, filterValues, fetchData]);

  const onPageChange = (newPage) => {
    setPage(newPage);
  };

  const handleEdit = (event, record) => {
    const url = `/digitalization/configurations/machine-information/${event}/${record.id}`;
    navigate(url);
  };

  const handleDelete = async (record) => {
    try {
      const response = await deleteData(
        `/api/digitalization/machine-maintenance/machine-information/${record.id}`,
      );

      if (response?.code === 200) {
        HandleNotification(
          "success",
          "Deleted",
          "Machine Issues deleted successfully",
        );
        setRefresh((prev) => prev + 1);
      }
    } catch (error) {
      HandleNotification("error", "Error", "Failed to delete Machine Issues");
    }
  };

  const tableColumns = [
    {
      title: "Issue Date",
      dataIndex: "issue_date",
      key: "issue_date",
      width: "150px",
      render: (text) => cglFormatDate(text, true),
    },
    {
      title: "Line Type",
      dataIndex: "line_type",
      key: "line_type",
      width: "100px",
      render: (text) =>
        text?.charAt(0)?.toUpperCase() + text?.slice(1) || "N/A",
    },
    {
      title: "Line",
      dataIndex: "line_name",
      key: "line_name",
      width: "100px",
    },
    {
      title: "Operation",
      dataIndex: "operation_name",
      key: "operation_name",
      width: "150px",
    },
    {
      title: "Machine ID",
      dataIndex: "machine_identifier",
      key: "machine_identifier",
      width: "130px",
    },
    {
      title: "Operator ID",
      dataIndex: "operator_id_card",
      key: "operator_id_card",
      width: "120px",
    },
    {
      title: "Fault",
      dataIndex: "machine_fault_type",
      key: "machine_fault_type",
      width: "150px",
    },
    {
      title: "Status",
      dataIndex: "status_label",
      key: "status_label",
      width: "100px",
    },
    {
      title: "Issued By",
      dataIndex: "created_by",
      key: "created_by",
      width: "150px",
    },
    {
      title: "Assign Status",
      dataIndex: ["tracker_info", "assigned_label"],
      key: "assign_status",
      width: "120px",
      render: (text, record) => record.tracker_info?.assigned_label || "N/A",
    },
    {
      title: "Assign Type",
      dataIndex: ["tracker_info", "assigned_type"],
      key: "assign_type",
      width: "120px",
      render: (text, record) => record.tracker_info?.assigned_type || "N/A",
    },
    {
      title: "Assignee",
      dataIndex: ["tracker_info", "mechanic_user_name"],
      key: "assignee_id",
      width: "120px",
      render: (text, record) => {
        return record.tracker_info?.mechanic_user_name || "N/A";
      },
    },
    {
      title: "Assignee ID",
      dataIndex: ["tracker_info", "mechanic_user_id_card"],
      key: "assignee_id",
      width: "120px",
      render: (text, record) => {
        const mechanic = record.tracker_info?.operator_info;
        return record.tracker_info?.mechanic_user_id_card || "N/A";
      },
    },
    {
      title: "Maintenance Started",
      dataIndex: ["tracker_info", "maintenance_started_at"],
      key: "maintenance_started",
      width: "170px",
      render: (text, record) =>
        record.tracker_info?.maintenance_started_at
          ? moment(record.tracker_info.maintenance_started_at).format(
              "DD-MMM-YYYY HH:mm:ss",
            )
          : "N/A",
    },
    {
      title: "Maintenance Ended",
      dataIndex: ["tracker_info", "maintenance_ended_at"],
      key: "maintenance_ended",
      width: "170px",
      render: (text, record) =>
        record.tracker_info?.maintenance_ended_at
          ? moment(record.tracker_info.maintenance_ended_at).format(
              "DD-MMM-YYYY HH:mm:ss",
            )
          : "N/A",
    },
    {
      title: "Duration",
      dataIndex: ["tracker_info", "maintenance_duration"],
      key: "duration",
      width: "100px",
      render: (text, record) =>
        record.tracker_info?.maintenance_duration || "N/A",
    },
    // {
    //   title: "Action",
    //   key: "action",
    //   width: "120px",
    //   render: (_, record) => (
    //     <Space size="small">
    //       <Button
    //         style={{ marginLeft: 5 }}
    //         size="small"
    //         onClick={(event) => {
    //           handleEdit("view", record);
    //         }}
    //       >
    //         View
    //       </Button>
    //       <Popconfirm
    //         title="Are you sure to delete this Machine Issue?"
    //         onConfirm={() => handleDelete(record)}
    //         okText="Yes"
    //         cancelText="No"
    //       >
    //         <Button style={{ marginLeft: 5 }} size="small" danger>
    //           Delete
    //         </Button>
    //       </Popconfirm>
    //     </Space>
    //   ),
    // },
  ];

  const memoizedCustomFilter = useMemo(
    () => (
      <FilterComponent
        tableData={tableData}
        filterAPI={onFilterChange}
        filterName={"machine-issues"}
        setShowFilter={setShowFilter}
        showFilter={showFilter}
      />
    ),
    [showFilter],
  );

  const addOrUpdateRoute = () => {
    const route = id
      ? `/digitalization/configurations/machine-information/edit/${id}`
      : `/digitalization/configurations/machine-information/store`;
    navigate(route);
  };

  const header = (
    <AppPageHeader
      extra={
        <ExtraContent
          refresh={refresh}
          setRefresh={setRefresh}
          handleAddItemOpen={addOrUpdateRoute}
          setShowFilter={setShowFilter}
          hideAddButton={true}
          // addButtonText={
          //   hasPermission([MACHINE_LINE_ISSUES_VIEW_PERMISSION])
          //     ? "Add New"
          //     : ""
          // }
          showFilter={showFilter}
          // hasImportButton={
          //   hasPermission([MACHINE_LINE_ISSUES_VIEW_PERMISSION])
          //     ? true
          //     : false
          // }
          // uploadAPI={IMPORT_MACHINE_INFO}
          // acceptedFileTypes={".xlsx"}
        />
      }
      title={<Title level={5}>Machine Issues</Title>}
    />
  );
  const content = (
    <>
      {memoizedCustomFilter}
      <ListTable
        columns={tableColumns}
        dataSource={tableData}
        loading={!tableData}
        pagination={{
          showSizeChanger: false,
          total: total,
          current: page,
          pageSize: pageSize,
          onChange: onPageChange,
        }}
        scroll={{ x: "max-content" }}
      />
    </>
  );
  return (
    <>
      <AppContent header={header} content={content} />
    </>
  );
};

export default MachineIssues;
